import React, { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { Alert, Box, Button, Chip, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import { formatCurrency, getDeptColor, getDeptShortName, sortDepartments } from '../../departmentIncomeTracker/utils/deptDisplay';
import { useElementSize } from '../../departmentIncomeTracker/utils/layoutHooks';
import { monthProgress, summariseDepartments, topDays, weekByWeek } from '../utils/departmentIncomeInsights';
import { compactInr, DEPT_ABBREVIATIONS, PANEL_SX, THEME } from '../utils/trackerConfig';
import { textFits, useFontsReady } from '../utils/textFit';

const BLUE = THEME.primary; // one blue for the whole page (the Accounts Manager's)
const head = { backgroundColor: BLUE, color: '#fff', fontWeight: 700, fontSize: 12, lineHeight: 1.2, py: 0.6, px: 0.75, textAlign: 'center', whiteSpace: 'normal', border: '1px solid rgba(255,255,255,0.3)' };
const cell = { fontSize: 12.5, py: 0.35, px: 0.75, textAlign: 'center', border: `1px solid ${THEME.grid}`, fontVariantNumeric: 'tabular-nums' };
const totalCell = { ...cell, fontWeight: 800, backgroundColor: THEME.total, borderTop: `2px solid ${THEME.grid}` };

// share (%) of the week table's width taken by its "Week 3  15–21" label column - wide enough to keep the label on one line
const WEEK_LABEL = 27;

function Caption({ children, hint }) {
  return (
    <Tooltip title={hint || ''} arrow disableHoverListener={!hint}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: THEME.primaryDark, mb: 0.25, cursor: hint ? 'help' : 'default' }}>
        {children}
      </Typography>
    </Tooltip>
  );
}

// `label` is the name as the table has worked out it fits (see `deptName` below); never broken in the middle of a word
const DeptName = ({ line, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-start', fontWeight: 700 }}>
    {line.deptId !== 'total' && <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, backgroundColor: getDeptColor(line.deptId) }} />}
    {line.deptId === 'total' ? 'Total' : label || getDeptShortName(line.deptId, line.deptName)}
  </Box>
);

const Change = ({ value, decimals = 1 }) =>
  value === null || value === undefined ? (
    <span style={{ color: '#94A3B8' }}>—</span>
  ) : (
    <Box component="span" sx={{ fontWeight: 700, color: value >= 0 ? '#047857' : '#B91C1C' }}>
      {value >= 0 ? '▲' : '▼'} {Math.abs(value).toFixed(decimals)}%
    </Box>
  );

/**
 * The area above the Accounts Manager's daily table on the Project Tracker: who the figures are for, then two small tables -
 * income by department (share of the total, average a day, the best day, the change against the same month last year) and
 * week by week (the sheet's four weeks). All of it is worked out from the department-income response, so it carries the Accounts
 * Manager's own numbers; nothing here is a second version of any figure.
 */
export default function ProjectDepartmentInfo({ responseData, loading, error, month, skillxOnly, onReload }) {
  const meta = responseData?.meta;
  const isFirstLoad = loading && !meta;

  const departments = useMemo(() => sortDepartments(meta?.departments || []), [meta]);
  const rows = useMemo(() => responseData?.data || [], [responseData?.data]);
  const lastYearRows = useMemo(() => meta?.lastYear?.data || [], [meta]);
  const { elapsedDays, daysInMonth } = useMemo(() => monthProgress(month), [month]);

  const summary = useMemo(() => summariseDepartments({ departments, rows, lastYearRows, elapsedDays }), [departments, rows, lastYearRows, elapsedDays]);
  const weeks = useMemo(() => weekByWeek({ departments, rows, elapsedDays, daysInMonth }), [departments, rows, elapsedDays, daysInMonth]);
  const several = departments.length > 1;

  // ---- small screens: the figures work out how wide their own box is ----
  // Wide: everything. Narrower: the type gets smaller, the less important columns (Share, Avg / day) leave, amounts go to lakh / crore
  // (only when the full amount would not fit its column) and the department names shorten. A line under the heading says what L / Cr mean.
  const [boxRef, , boxW] = useElementSize();
  useFontsReady();
  const w = boxW === null ? 700 : boxW;
  const fs = w < 420 ? 11 : w < 560 ? 11.5 : 12.5;
  const showAvg = w >= 440;
  const showShare = several && w >= 520;
  const COLUMNS = [
    ['dept', several ? 16 : 21],
    ['income', several ? 15 : 16],
    ...(showShare ? [['share', 10]] : []),
    ...(showAvg ? [['avg', several ? 12 : 14]] : []),
    ['best', several ? 17 : 24],
    ['ly', several ? 16 : 14],
    ['chg', several ? 14 : 11],
  ];
  const colTotal = COLUMNS.reduce((t, [, x]) => t + x, 0);
  const byDeptCols = COLUMNS.map(([, x]) => (x / colTotal) * 100);
  const weekShare = (100 - WEEK_LABEL) / (departments.length + (several ? 1 : 0));
  // Decided COLUMN by column: a column writes its amounts in full whenever the biggest of them fits it; only a column that cannot is written
  // in lakh / crore (hover shows the full amount). The "₹ " stays while every column fits with it, otherwise it goes (the heading says ₹).
  const colPx = Object.fromEntries(COLUMNS.map(([k], i) => [k, (w * byDeptCols[i]) / 100]));
  const weekPx = (w * weekShare) / 100;
  const lines = [summary.total, ...summary.lines];
  const biggest = {
    income: Math.max(0, ...lines.map((l) => l.income || 0)),
    avg: Math.max(0, ...lines.map((l) => l.dailyAvg || 0)),
    best: Math.max(0, ...lines.map((l) => l.bestDay?.amount || 0)),
    ly: Math.max(0, ...lines.map((l) => l.lastYear || 0)),
    week: Math.max(0, weeks.month?.total || 0, ...weeks.weeks.map((x) => x.total || 0)),
  };
  const pxOf = (k) => (k === 'week' ? weekPx : colPx[k]);
  const showable = Object.keys(biggest).filter((k) => k === 'week' || colPx[k] !== undefined);
  const rupee = showable.every((k) => textFits(formatCurrency(Math.round(biggest[k])), pxOf(k), fs, true, 12));
  const compactCols = new Set(showable.filter((k) => !textFits(rupee ? formatCurrency(Math.round(biggest[k])) : Math.round(biggest[k]).toLocaleString('en-IN'), pxOf(k), fs, true, 12)));
  const compact = !rupee;
  const money = (v, k) => {
    const whole = Math.round(Number(v) || 0);
    if (compactCols.has(k)) return `${rupee ? '₹ ' : ''}${compactInr(v)}`;
    return rupee ? formatCurrency(whole) : whole.toLocaleString('en-IN');
  };
  // a figure that was shortened keeps its full amount on hover
  const amt = (v, k) => <span title={compactCols.has(k) ? formatCurrency(Math.round(Number(v) || 0)) : undefined}>{money(v, k)}</span>;
  const changeDecimals = w < 520 ? 0 : 1;
  // a department's full short name where it fits its column (the first table's also carries a coloured dot), else its abbreviation
  const nameIn = (d, px, dot) => {
    const full = getDeptShortName(d.deptId, d.deptName);
    return textFits(full, px - (dot ? 18 : 0), fs, true, 8) ? full : DEPT_ABBREVIATIONS[d.deptId] || full;
  };
  const deptName = (d) => nameIn(d, weekPx, false);
  // one department has little to compare, so its team also gets the biggest days - rows are a little tighter to leave room for them
  // fixed layout + percentage columns: the tables always fit the width they are given (they never scroll sideways); long text wraps
  const tableSx = { tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse', '& td, & th': { px: 0.5, overflowWrap: 'anywhere' }, '& td': { py: several ? 0.35 : 0.2, fontSize: fs }, '& th': { fontSize: fs - 0.5 } };
  const weekCols = [WEEK_LABEL, ...departments.map(() => weekShare), ...(several ? [weekShare] : [])];
  // a single department has little to compare, so its team also gets the two best days (room is there; several departments fill the area already)
  const best = useMemo(() => (departments.length > 1 ? [] : topDays({ rows, elapsedDays, limit: 2 })), [departments.length, rows, elapsedDays]);
  // "Feb 2025": the same month a year earlier; while the month is only part-way through, only the same days of it are compared
  const lastYearLabel = meta?.lastYear?.month ? dayjs(meta.lastYear.month).format('MMM YYYY') : 'Last year';
  const partMonth = elapsedDays < daysInMonth;

  const handleExport = useCallback(() => {
    const headerRow = ['Date', ...departments.map((d) => getDeptShortName(d.deptId, d.deptName)), 'Total'];
    const body = rows.map((r) => [r.date, ...departments.map((d) => Number(r.byDept?.[d.deptId]) || 0), Number(r.total) || 0]);
    const totalRow = ['Total', ...departments.map((d) => rows.reduce((t, r) => t + (Number(r.byDept?.[d.deptId]) || 0), 0)), rows.reduce((t, r) => t + (Number(r.total) || 0), 0)];
    const sheet = XLSX.utils.aoa_to_sheet([headerRow, ...body, totalRow]);
    sheet['!cols'] = headerRow.map(() => ({ wch: 16 }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'Daily Breakdown');
    XLSX.writeFile(workbook, `project-department-income-${dayjs(month).format('YYYY-MM')}${skillxOnly ? '-skillx' : ''}.xlsx`);
  }, [departments, rows, month, skillxOnly]);

  const scopeLabel = skillxOnly ? 'SkillX' : meta?.access === 'department' ? 'Your department' : 'Project departments';

  const summaryRows = several ? [...summary.lines, summary.total] : summary.lines;

  return (
    <Box ref={boxRef} sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
      {/* ---------- title bar ---------- */}
      <Box
        sx={{ ...PANEL_SX, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, px: 1.5, py: 0.75 }}
      >
        <AccountBalanceRoundedIcon sx={{ color: BLUE }} fontSize="small" />
        <Typography sx={{ fontWeight: 800, color: THEME.primaryDark, lineHeight: 1.2 }}>Accounts Manager</Typography>
        <Chip label={scopeLabel} size="small" sx={{ height: 20, fontWeight: 700, color: BLUE, backgroundColor: THEME.primarySoft }} />
        <Tooltip title="Income collected per department for the whole month, credited to the department of whoever entered the payment (Accounts Manager rule). Little Genie includes STC. Click a cell of the daily table for the payments behind it. Week chips do not apply here." arrow>
          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
            {dayjs(month).format('MMMM YYYY')} · {elapsedDays >= daysInMonth ? 'full month' : `day ${elapsedDays} of ${daysInMonth}`}
          </Typography>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" startIcon={<FileDownloadRoundedIcon />} onClick={handleExport} disabled={!rows.length} sx={{ fontWeight: 700 }}>
          Export
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onReload}>
              Retry
            </Button>
          }
        >
          Could not load the department income: {error}
        </Alert>
      )}

      {!error && isFirstLoad && <Skeleton variant="rectangular" height={300} sx={{ borderRadius: '10px' }} />}

      {!error && !isFirstLoad && (
        <>
          {/* ---------- income by department ---------- */}
          <Box>
            <Caption hint={`${lastYearLabel} is the same month a year earlier${partMonth ? ` - only its days 1-${elapsedDays}, so a month that is part-way through is not set against a whole one` : ''}. Change = this month's income against it. Share = the department's part of the combined income of the departments shown. Best day = the biggest single day so far.`}>
              Income by department
            </Caption>
            {compact && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, lineHeight: 1.2 }}>
                Amounts in ₹ · L = lakh (1,00,000) · Cr = crore (1,00,00,000). Hover a figure for the full amount.
              </Typography>
            )}
            <Table size="small" sx={tableSx}>
              <colgroup>
                {byDeptCols.map((w, i) => (
                  <col key={i} style={{ width: `${w}%` }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...head, textAlign: 'left' }}>Department</TableCell>
                  <TableCell sx={head}>Income</TableCell>
                  {showShare && <TableCell sx={head}>Share</TableCell>}
                  {showAvg && <TableCell sx={head}>Avg / day</TableCell>}
                  <TableCell sx={head}>Best day</TableCell>
                  <TableCell sx={head}>
                    {lastYearLabel}
                    {partMonth && <Box component="span" sx={{ display: 'block', fontWeight: 500, fontSize: 10.5, opacity: 0.9 }}>days 1–{elapsedDays}</Box>}
                  </TableCell>
                  <TableCell sx={head}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryRows.map((l) => {
                  const isTotal = l.deptId === 'total';
                  const c = isTotal ? totalCell : cell;
                  return (
                    <TableRow key={l.deptId} hover={!isTotal}>
                      <TableCell sx={{ ...c, textAlign: 'left' }}>
                        <DeptName line={l} label={l.deptId === 'total' ? undefined : nameIn(l, colPx.dept, true)} />
                      </TableCell>
                      <TableCell sx={{ ...c, fontWeight: 800, color: THEME.primaryDark }}>{amt(l.income, 'income')}</TableCell>
                      {showShare && <TableCell sx={{ ...c, whiteSpace: 'nowrap' }}>{l.share === null ? '—' : `${l.share.toFixed(1)}%`}</TableCell>}
                      {showAvg && <TableCell sx={c}>{amt(l.dailyAvg, 'avg')}</TableCell>}
                      <TableCell sx={c}>
                        {l.bestDay ? (
                          <>
                            {amt(l.bestDay.amount, 'best')}
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                              {dayjs(l.bestDay.date).format('D MMM')}
                            </Typography>
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={c}>{amt(l.lastYear, 'ly')}</TableCell>
                      <TableCell sx={{ ...c, whiteSpace: 'nowrap' }}>
                        <Change value={l.change} decimals={changeDecimals} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {/* ---------- week by week ---------- */}
          <Box>
            <Caption hint="The sheet's four weeks: days 1-7, 8-14, 15-21 and 22 to the month end.">Week by week</Caption>
            <Table size="small" sx={tableSx}>
              <colgroup>
                {weekCols.map((w, i) => (
                  <col key={i} style={{ width: `${w}%` }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...head, textAlign: 'left' }}>Week</TableCell>
                  {departments.map((d) => (
                    <TableCell key={d.deptId} sx={head}>
                      {deptName(d)}
                    </TableCell>
                  ))}
                  {several && <TableCell sx={head}>Total</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {weeks.weeks.map((w) => (
                  <TableRow key={w.key} hover>
                    <TableCell sx={{ ...cell, textAlign: 'left', fontWeight: 700 }}>
                      {w.label}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                        {w.range}
                      </Typography>
                    </TableCell>
                    {departments.map((d) => (
                      <TableCell key={d.deptId} sx={cell}>
                        {w.started ? money(w.byDept[d.deptId], 'week') : '–'}
                      </TableCell>
                    ))}
                    {several && <TableCell sx={{ ...cell, fontWeight: 800 }}>{w.started ? money(w.total, 'week') : '–'}</TableCell>}
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ ...totalCell, textAlign: 'left' }}>Month</TableCell>
                  {departments.map((d) => (
                    <TableCell key={d.deptId} sx={totalCell}>
                      {amt(weeks.month.byDept[d.deptId], 'week')}
                    </TableCell>
                  ))}
                  {several && <TableCell sx={totalCell}>{amt(weeks.month.total, 'week')}</TableCell>}
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {/* ---------- biggest days (single-department view) ---------- */}
          {best.length > 0 && (
            <Box>
              <Caption hint="The two days with the most income so far, and each one's share of the month so far.">Biggest days</Caption>
              <Table size="small" sx={tableSx}>
                <colgroup>
                  {[10, 34, 28, 28].map((w, i) => (
                    <col key={i} style={{ width: `${w}%` }} />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell sx={head}>#</TableCell>
                    <TableCell sx={{ ...head, textAlign: 'left' }}>Day</TableCell>
                    <TableCell sx={head}>Income</TableCell>
                    <TableCell sx={head}>Share of month</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {best.map((d, i) => (
                    <TableRow key={d.date} hover>
                      <TableCell sx={{ ...cell, color: '#64748B', fontWeight: 700 }}>{i + 1}</TableCell>
                      <TableCell sx={{ ...cell, textAlign: 'left', fontWeight: 700 }}>{dayjs(d.date).format('ddd, D MMM')}</TableCell>
                      <TableCell sx={{ ...cell, fontWeight: 800, color: THEME.primaryDark }}>{amt(d.amount, 'best')}</TableCell>
                      <TableCell sx={cell}>{d.share === null ? '—' : `${d.share.toFixed(1)}%`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
